import { useState, useRef } from 'react';
import { Camera, Upload } from 'lucide-react';
import { useSelector } from 'react-redux';
import { supabase } from '../supabase/client';
import { generateImageUuid } from '../utils/imageUrls';
import ImageDisplay from './ImageDisplay';

/**
 * ImageTest - Test component for image display with camera/upload buttons
 * Resizable box centered on screen for testing different sizes
 */
function ImageTest() {
  const [imageData, setImageData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const currentUserId = useSelector((s) => s.user.authUserId);

  const uploadImage = async (file) => {
    const uuid = generateImageUuid();
    
    // RLS policy requires path to be {userId}/{filename}
    const path = `${currentUserId}/${uuid}`;
    
    const { error } = await supabase.storage.from('chat-images').upload(path, file, { upsert: true });
    if (error) {
      console.error('[ImageTest] Upload error:', error);
      throw error;
    }
    // Return just the UUID for cleaner JSON storage
    return uuid;
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);

    // Create preview URL before upload
    const previewUrl = URL.createObjectURL(file);

    // Set preview data immediately
    const previewData = {
      imageUUID: null,
      source: 'upload',
      userId: currentUserId,
      bucket: 'chat-images',
      previewUrl,
      loading: true
    };
    setImageData(previewData);

    try {
      const uuid = await uploadImage(file);
      const newData = {
        imageUUID: uuid,
        source: 'upload',
        userId: currentUserId,
        bucket: 'chat-images',
        previewUrl, // Keep previewUrl until image loads from storage
        loading: false
      };
      setImageData(newData);
    } catch (error) {
      console.error('[ImageTest] Upload error:', error);
      setImageData(null);
    } finally {
      setUploading(false);
      // Clean up preview URL
      URL.revokeObjectURL(previewUrl);
    }
  };

  const handleCameraCapture = async () => {
    setUploading(true);
    try {
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });

      streamRef.current = stream;

      // Create video element to capture frame
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;

      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });

      // Capture frame after a short delay
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);

      // Stop camera
      stream.getTracks().forEach(track => track.stop());

      // Create preview URL from canvas
      const previewUrl = canvas.toDataURL('image/jpeg', 0.9);

      // Set preview data immediately
      const previewData = {
        imageUUID: null,
        source: 'camera',
        userId: currentUserId,
        bucket: 'chat-images',
        previewUrl,
        loading: true
      };
      setImageData(previewData);

      // Convert to blob and upload
      canvas.toBlob(async (blob) => {
        if (!blob) {
          console.error('[ImageTest] Failed to create blob from canvas');
          setUploading(false);
          return;
        }

        try {
          const uuid = await uploadImage(blob);
          const newData = {
            imageUUID: uuid,
            source: 'camera',
            userId: currentUserId,
            bucket: 'chat-images',
            previewUrl, // Keep previewUrl until image loads from storage
            loading: false
          };
          setImageData(newData);
        } catch (error) {
          console.error('[ImageTest] Upload error:', error);
          setImageData(null);
        } finally {
          setUploading(false);
        }
      }, 'image/jpeg', 0.9);
    } catch (error) {
      console.error('[ImageTest] Camera error:', error);
      setUploading(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleClear = () => {
    setImageData(null);
  };

  return (
    <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 min-h-[300px] min-w-[300px] max-h-[80vh] max-w-[80vw] border-2 border-border bg-card rounded-lg shadow-lg flex flex-col p-2.5 resize overflow-hidden">
      {/* Image display square - scales with parent */}
      <div className="flex justify-center flex-1 min-h-0">
        <div className="h-full w-full border-2 border-border rounded-lg overflow-hidden relative">
          <ImageDisplay
            imageData={imageData}
            className="h-full w-full"
          />
        </div>
      </div>

      {/* Rectangle at bottom with buttons */}
      <div className="h-[80px] w-full flex items-center justify-around mt-2.5">
        {/* Camera button */}
        <button
          onClick={handleCameraCapture}
          className="h-12 w-12 flex items-center justify-center rounded-lg bg-white shadow-md hover:bg-gray-100 transition"
        >
          <Camera size={24} className="text-gray-700" />
        </button>

        {/* File upload button */}
        <button
          onClick={handleFileButtonClick}
          className="h-12 w-12 flex items-center justify-center rounded-lg bg-white shadow-md hover:bg-gray-100 transition"
        >
          <Upload size={24} className="text-gray-700" />
        </button>

        {/* Clear button */}
        <button
          onClick={handleClear}
          className="h-12 w-12 flex items-center justify-center rounded-lg bg-red-500 shadow-md hover:bg-red-600 transition"
        >
          <span className="text-white text-sm">✕</span>
        </button>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />
    </div>
  );
}

export default ImageTest;
